// =============================================================================
// useInstallmentData
// =============================================================================
// PURPOSE: Fetches all payment plan leads (active + abandoned) via Supabase RPC.
//
// ARCHITECTURE:
// - Calls get_payment_plan_status RPC with organization_id + optional user_id (RBAC)
// - Returns ALL leads (active + abandoned) — frontend splits by tab
// - SWR cache: show localStorage data immediately, fetch fresh in background
// - No polling — data refreshes on page visit/navigation
//
// RPC: sql/get_payment_plan_status.sql
// =============================================================================

import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { supabase } from "../../lib/supabase";
import { logger } from "../../lib/logger";
import { CACHE_VERSION } from "../../lib/cacheVersion";
import type { InstallmentLead } from "../useOperationsAnalytics";

interface RpcRow {
  lead_name: string;
  lead_email: string;
  lead_phone: string | null;
  lead_id: string | null;
  closer_name: string | null;
  setter_name: string | null;
  lead_instagram: string | null;
  conversation_id: string | null;
  program: string | null;
  total_contract: number;
  cash_paid: number;
  remaining: number;
  next_payment: string | null;
  next_payment_amount: number | null;
  last_payment: string | null;
  days_overdue: number | null;
  is_overdue: boolean | null;
  close_date: string | null;
  is_abandoned: boolean;
  abandoned_at: string | null;
  abandon_reason: string | null;
  abandon_notes: string | null;
  abandon_called_by: string | null;
  closer_pif_count: number | null;
  closer_pif_revenue: number | null;
  setter_pif_count: number | null;
  setter_pif_revenue: number | null;
  closer_id: string | null;
  setter_id: string | null;
}

export interface InstallmentTotals {
  totalCollected: number;
  totalOutstanding: number;
  totalContractValue: number;
  overdueCount: number;
  leadCount: number;
  abandonedCount: number;
  missingNextDateCount: number;
  missingNextAmountCount: number;
}

export interface RepStats {
  name: string;
  planCount: number;
  planRevenue: number;
  cashCollected: number;
  pifCount: number;
  pifRevenue: number;
}

export interface UseInstallmentDataReturn {
  installmentLeads: InstallmentLead[];
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
  totals: InstallmentTotals;
  closerStats: RepStats[];
  setterStats: RepStats[];
}

const CACHE_KEY_PREFIX = `altara_${CACHE_VERSION}_installments_`;
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

interface CacheEntry {
  rows: RpcRow[];
  timestamp: number;
}

function getCacheKey(orgId: string, userId?: string): string {
  return `${CACHE_KEY_PREFIX}${orgId}${userId ? `_${userId}` : ""}`;
}

function readCache(key: string): RpcRow[] | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const entry: CacheEntry = JSON.parse(raw);
    // Return cached data regardless of age (SWR — always show stale)
    return entry.rows;
  } catch {
    return null;
  }
}

function isCacheFresh(key: string): boolean {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return false;
    const entry: CacheEntry = JSON.parse(raw);
    return Date.now() - entry.timestamp < CACHE_TTL_MS;
  } catch {
    return false;
  }
}

function writeCache(key: string, rows: RpcRow[]): void {
  try {
    const entry: CacheEntry = { rows, timestamp: Date.now() };
    localStorage.setItem(key, JSON.stringify(entry));
  } catch {
    // localStorage full or unavailable — silently ignore
  }
}

function mapRow(r: RpcRow): InstallmentLead {
  return {
    leadName: r.lead_name || "",
    leadEmail: r.lead_email || "",
    leadId: r.lead_id || undefined,
    leadPhone: r.lead_phone || undefined,
    program: r.program || "",
    totalContract: Number(r.total_contract) || 0,
    cashPaidSoFar: Number(r.cash_paid) || 0,
    lastPaymentDate: r.last_payment || "",
    nextPaymentDate: r.next_payment || "",
    nextPaymentAmount: Number(r.next_payment_amount) || 0,
    isFullyPaid: false, // RPC only returns unpaid or abandoned leads
    closerName: r.closer_name || "",
    setterName: r.setter_name || undefined,
    leadInstagram: r.lead_instagram || undefined,
    conversationId: r.conversation_id || undefined,
    daysOverdue: r.days_overdue ?? undefined,
    isOverdue: r.is_overdue ?? undefined,
    closeDate: r.close_date || undefined,
    isAbandoned: r.is_abandoned || false,
    abandonedAt: r.abandoned_at || undefined,
    abandonReason: r.abandon_reason || undefined,
    abandonNotes: r.abandon_notes || undefined,
    abandonCalledBy: r.abandon_called_by || undefined,
    closerPifCount: Number(r.closer_pif_count) || 0,
    closerPifRevenue: Number(r.closer_pif_revenue) || 0,
    setterPifCount: Number(r.setter_pif_count) || 0,
    setterPifRevenue: Number(r.setter_pif_revenue) || 0,
    closerId: r.closer_id || undefined,
    setterId: r.setter_id || undefined,
  };
}

function computeRepStats(leads: InstallmentLead[], role: "closer" | "setter"): RepStats[] {
  const map = new Map<string, RepStats>();

  for (const lead of leads) {
    if (lead.isAbandoned) continue; // Exclude abandoned from rep stats
    const name = role === "closer" ? lead.closerName : lead.setterName || "";
    if (!name) continue;

    if (!map.has(name)) {
      map.set(name, {
        name,
        planCount: 0,
        planRevenue: 0,
        cashCollected: 0,
        pifCount: role === "closer" ? lead.closerPifCount || 0 : lead.setterPifCount || 0,
        pifRevenue: role === "closer" ? lead.closerPifRevenue || 0 : lead.setterPifRevenue || 0,
      });
    }

    const stats = map.get(name)!;
    stats.planCount++;
    stats.planRevenue += lead.totalContract;
    stats.cashCollected += lead.cashPaidSoFar;
  }

  return Array.from(map.values()).sort((a, b) => b.planCount - a.planCount);
}

export function useInstallmentData(
  organizationId: string | undefined,
  userId?: string
): UseInstallmentDataReturn {
  const [rows, setRows] = useState<RpcRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const hasFetchedRef = useRef(false);

  const cacheKey = organizationId ? getCacheKey(organizationId, userId) : "";

  // Load from cache on mount (SWR: show stale data immediately)
  useEffect(() => {
    if (!cacheKey) return;
    const cached = readCache(cacheKey);
    if (cached && cached.length > 0) {
      setRows(cached);
      setIsLoading(false);
    }
  }, [cacheKey]);

  const fetchData = useCallback(async () => {
    if (!organizationId || !supabase) return;

    abortRef.current?.abort();
    abortRef.current = new AbortController();

    try {
      // Only show loading spinner if we have no cached data
      if (rows.length === 0) {
        setIsLoading(true);
      }
      setError(null);

      const { data, error: rpcError } = await supabase.rpc("get_payment_plan_status", {
        p_organization_id: organizationId,
        ...(userId ? { p_user_id: userId } : {}),
      });

      if (rpcError) throw rpcError;

      const freshRows = (data as RpcRow[]) || [];
      setRows(freshRows);
      writeCache(cacheKey, freshRows);
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      const message = err instanceof Error ? err.message : "Failed to load installment data";
      logger.error("Installment data fetch error:", err);
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [organizationId, userId, cacheKey, rows.length]);

  // Fetch on mount — skip if cache is fresh
  useEffect(() => {
    if (!organizationId) return;
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    if (isCacheFresh(cacheKey)) {
      // Cache is fresh — don't refetch
      return;
    }

    // SWR: fetch in background (cached data already showing)
    fetchData();

    return () => {
      abortRef.current?.abort();
    };
  }, [organizationId, cacheKey, fetchData]);

  // Reset when org/user changes
  useEffect(() => {
    hasFetchedRef.current = false;
  }, [organizationId, userId]);

  const installmentLeads = useMemo((): InstallmentLead[] => rows.map(mapRow), [rows]);

  const totals = useMemo((): InstallmentTotals => {
    const activeRows = rows.filter((r) => !r.is_abandoned);
    return {
      totalCollected: activeRows.reduce((s, r) => s + (Number(r.cash_paid) || 0), 0),
      totalOutstanding: activeRows.reduce((s, r) => s + (Number(r.remaining) || 0), 0),
      totalContractValue: activeRows.reduce((s, r) => s + (Number(r.total_contract) || 0), 0),
      overdueCount: activeRows.filter((r) => r.is_overdue).length,
      leadCount: activeRows.length,
      abandonedCount: rows.filter((r) => r.is_abandoned).length,
      missingNextDateCount: activeRows.filter((r) => !r.next_payment && !r.is_abandoned).length,
      missingNextAmountCount: activeRows.filter(
        (r) => (!r.next_payment_amount || Number(r.next_payment_amount) === 0) && !r.is_abandoned
      ).length,
    };
  }, [rows]);

  const closerStats = useMemo(
    () => computeRepStats(installmentLeads, "closer"),
    [installmentLeads]
  );
  const setterStats = useMemo(
    () => computeRepStats(installmentLeads, "setter"),
    [installmentLeads]
  );

  return {
    installmentLeads,
    isLoading,
    error,
    refresh: fetchData,
    totals,
    closerStats,
    setterStats,
  };
}
